import React, { useRef, useMemo, useState } from 'react';
import {
    SubscriptionFormSection as SubscriptionFormDS,
    ModalSmall,
    ModalRef,
    ModalSmallProps,
} from '@solublestudio/legalbrave-design-system';

import { useUser, UserContext } from 'api-base/auth';
import { useTranslate } from '../../hooks/useTranslate';
import { Config } from 'api-base/lib/types';
import { useUtms } from '../../hooks/useUtms';

import { useStripe } from '@stripe/react-stripe-js';

import { useRouter } from 'next/router';

import {
    getPlanNameForTracking,
    getPriceForTracking,
} from '@solublestudio/legalbrave-design-system/src/utils/tracking';

import StripePaymentForm from '../StripePaymentForm';
import PaypalPaymentForm from '../PaypalPaymentForm';

import { stripePromise } from '../../clients/stripe';

const CONSTANT = {
    promo: 'main--95-25',
};

const INITIAL_STEP = 1;

const SubscriptionFormSection = ({
    isPremium,
    is9525,
    globalConfig,
    ...props
}: {
    isPremium: boolean;
    is9525: boolean;
    globalConfig: Config;
    formId?: string;
}) => {
    const [t] = useTranslate();
    const { user, isLoading }: UserContext = useUser();
    const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
    const [modalProps, setModalProps] = useState({});
    const [cookies] = useUtms(globalConfig.web.cookies.utms);

    const baseUrl = `${process.env.BASE_URL}`;
    const checkExistUrl = baseUrl + '/api/users/check-exists/';
    const priceMonthlyId = isPremium
        ? process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM
        : process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_ESSENTIAL;
    const priceSemestralId = isPremium
        ? process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_SEMESTRAL
        : process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_ESSENTIAL_SEMESTRAL;

    const pricesDifference = isPremium ? '$39.99' : '$24.99';

    const cancelSubscriptionUrl = baseUrl + '/api/subscriptions/';
    const checkPromoUrl = baseUrl + '/api/check-promo/';
    const redirectPath = '/api/auth/signup';
    const redirectUrl = baseUrl + redirectPath;
    const leadUrl = baseUrl + '/api/lead/';
    const logoutPath = '/api/auth/force-logout';

    const priceMonthly = isPremium
        ? globalConfig.web.prices.premium
        : globalConfig.web.prices.essential;
    const priceSemestral = isPremium
        ? globalConfig.web.prices.premium_semestral
        : globalConfig.web.prices.essential_semestral;

    const states = Object.keys(globalConfig.crm.country.states).map((key) => ({
        name: key,
        abbreviation: globalConfig.crm.country.states?.[key],
    }));

    const price = is9525 ? globalConfig.web.prices.unique_plan : '';

    const phone_prefix = globalConfig.web.phone_prefix;

    const plan = is9525 ? 'UNIQUE_PLAN' : 'ERROR';

    const plans = {
        monthly: {
            stripePrice: priceMonthlyId,
            price: priceMonthly,
            plan: isPremium ? 'PREMIUM' : 'ESSENTIAL',
            extraCharge: '',
        },
        annual: {
            stripePrice: priceSemestralId,
            price: priceSemestral,
            plan: isPremium ? 'PREMIUM_SEMESTRAL' : 'ESSENTIAL_SEMESTRAL',
            extraCharge: '',
        },
        promo: {
            stripePrice: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PROMO,
            price,
            plan,
            extraCharge: process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PROMO_INIT,
        },
    };

    // TODO: Check which components are using this hiddenFields and pass this data as props
    const hiddenFields = useMemo(
        () => [
            {
                key: 'checkExistUrl',
                value: checkExistUrl,
            },
            {
                key: 'stripePricesId',
                value: `${priceMonthlyId}--${priceSemestralId}`,
            },
            {
                key: 'prices',
                value: `${priceMonthly}--${priceSemestral}`,
            },
            {
                key: 'price',
                value: price,
            },
            {
                key: 'pricesDifference',
                value: pricesDifference,
            },
            {
                key: 'plan',
                value: plan,
            },
            {
                key: 'plans',
                value: isPremium
                    ? 'PREMIUM--PREMIUM_SEMESTRAL'
                    : 'ESSENTIAL--ESSENTIAL_SEMESTRAL',
            },
            {
                key: 'cancelSubscriptionUrl',
                value: cancelSubscriptionUrl,
            },
            {
                key: 'checkPromoUrl',
                value: checkPromoUrl,
            },
            {
                key: 'redirectUrl',
                value: redirectUrl,
            },
            {
                key: 'phone',
                value: phone_prefix,
            },
            {
                key: 'phone_2',
                value: phone_prefix,
            },
            {
                key: 'leadUrl',
                value: leadUrl,
            },
            {
                key: 'country',
                value: globalConfig.crm.country.name,
            },
            {
                key: 'cookies',
                value: cookies,
            },
        ],
        [cookies],
    );

    const router = useRouter();
    const stripe = useStripe();

    const typeForm =
        process.env.NEXT_PUBLIC_DATOCMS_ENVIRONMENT === CONSTANT.promo
            ? 'promo'
            : 'default';

    const paymentCardRef = useRef(null);

    const onMakePurchase = async (paymentData, form) => {
        const { element, dataCard, fullNameCard } = paymentData;

        try {
            const res = await stripe?.createToken(element);

            if (res) {
                const data = await stripe?.createPaymentMethod({
                    type: 'card',
                    card: element,
                });

                if (data?.paymentMethod) {
                    let idPm = data?.paymentMethod.id;

                    await createUser({ idPm, dataCard, fullNameCard }, form);
                } else {
                    throw new Error(res.error?.message);
                }
            }
        } catch (e) {
            throw Error(e.message);
        }
    };

    const createUser = async (paymentData, form) => {
        const { idPm, dataCard } = paymentData;
        try {
            const res = await fetch('/api/custumer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
                body: JSON.stringify({
                    email: form.email,
                    name: form.name,
                    payment_method: idPm,
                    plan: form.plan,
                    invoice_settings: {
                        default_payment_method: idPm,
                    },
                    coupon: dataCard.codeApplied ? dataCard.idCode : '',
                }),
            });
            if (res.status === 200) {
                await createSalesUser(form);
            } else {
                throw Error(res.message ?? 'Charge declined by card issuer.');
            }
        } catch (e) {
            throw Error('Charge declined by card issuer.');
        }
    };

    const modalRef = useRef<ModalRef>();
    const loadingModalProps: ModalSmallProps = useMemo(
        () => ({
            iconName: 'loading',
            description: 'We are processing your data…',
            iconType: 'loading',
            hideButtons: true,
        }),
        [],
    );

    const createSalesUser = async (form) => {
        modalRef?.current?.toggle();
        const res = await fetch('/api/users', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify(form),
        });

        if (res.status !== 200) {
            modalRef?.current?.toggle();
            throw Error(
                'Error trying to creating your subscription, please try again or get in touch with our team',
            );
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'purchase',
            product: getPlanNameForTracking(form.plan), // essential, premium, promo
            price: getPriceForTracking(form.plan),
        });

        modalRef?.current?.toggle();
        const url = new URL(`${redirectUrl}?email=${form.email}`);
        router.push(url);
    };

    const createLead = async (form) => {
        const response = await fetch('/api/lead/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: form.name,
                lastName: form.lastName,
                email: form.email,
                phone: form.phone,
                address: form.address,
                city: form.city,
                LeadSource: form.LeadSource,
                source: form.source,
            }),
        });

        const leadResponse = await response.json();

        if (leadResponse.message === 'OK') {
            setCurrentStep(2);
            return;
        }

        throw Error('Error trying to reigster User, please try again.');
    };

    const onSubmit = async (form) => {
        const { email, phone } = form;

        const params = new URLSearchParams({
            email: email,
            phone: phone,
        }).toString();

        const response = await fetch(`/api/users/check-exists/?${params}`);

        const existRecord = await response.json();

        if (!existRecord) {
            await createLead(form);
            return;
        }

        throw Error('User already exists');
    };

    const onValidatePromo = async (dataCard) => {
        const URL = `${
            hiddenFields.find((field: any) => field.key === 'checkPromoUrl')
                ?.value
        }${dataCard.codePromo}`;

        const res = await fetch(URL);

        const data = await res.json();

        if (
            paymentCardRef?.current &&
            typeof paymentCardRef?.current?.setCard === 'function'
        ) {
            paymentCardRef.current.setCard({
                ...dataCard,
                nameIfExist: data ? data.name : 'Please enter a valid code.',
                codeApplied: data ? true : false,
                idCode: data ? data.id : '',
            });

            return;
        }

        throw Error('Please enter a valid code.');
    };

    const onApprove = async (data, actions, form) => {
        if (!data?.subscriptionID) {
            throw Error('Error on subscription, please try again.');
        }

        let formData = {
            ...form,
            subscriptionID: data.subscriptionID,
        };

        await createSalesUser(formData);
    };

    const createSubscription = async (data, actions, form) => {
        const res = await fetch('/api/create-subscription', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                plan: form.plan,
                name: form.name,
                lastName: form.lastName,
                email: form.email,
                phone: form.phone,
                address: form.address,
                city: form.city,
                state: form.state,
                zipcode: form.zipcode,
            }),
        });

        const subscriptionData = await res.json();

        // Return the subscription ID to paypal
        return subscriptionData?.id;
    };

    const onError = (data) => {
        throw Error('Cancel subscription, please try again.');
    };

    // Uses the right disclaimer text based on plan selected and current price
    const disclaimerText = (price, plan) => {
        const key = plan.endsWith('SEMESTRAL') ? 'semianual' : 'month';
        return t(`checkout.disclaimer.${key}`, {
            price,
        });
    };

    if (isLoading || typeof document === 'undefined') return null;

    const createSession = async (
        plan,
        email,
    ): Promise<{ sessionData: string; id: string }> => {
        const res = await fetch('/api/payment/session/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                plan,
                email,
            }),
        });
        if (res.status !== 200) {
            throw new Error((await res.json()).message);
        }

        const { sessionData, sessionId } = await res.json();

        // Return data for Adyen
        return {
            sessionData,
            id: sessionId,
        };
    };

    return (
        <>
            <ModalSmall ref={modalRef} {...loadingModalProps}></ModalSmall>
            <SubscriptionFormDS
                {...props}
                onSubmit={onSubmit}
                hiddenFields={hiddenFields}
                currentStep={currentStep}
                typeForm={typeForm}
                plans={plans}
                paymentCardRef={paymentCardRef}
                client="adyen"
                states={states}
                stripe={{
                    onMakePurchase,
                    onValidatePromo,
                }}
                paypal={{
                    createSubscription,
                    onApprove,
                    onCancel: onError,
                    onError,
                    // createOrder,
                }}
                disclaimerText={disclaimerText}
                adyen={{
                    onLoad: createSession,
                    onApprove: createSalesUser,
                }}
            />
        </>
    );
};

export default function SubscriptionFormSectionWrapper(props) {
    return (
        <StripePaymentForm stripe={stripePromise}>
            <PaypalPaymentForm country="US">
                <SubscriptionFormSection {...props} />
            </PaypalPaymentForm>
        </StripePaymentForm>
    );
}
